import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Repeat, Settings2, UserPlus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth, useCan } from "@/context/AuthContext";
import { addDays, dayLabel, mondayOf, runsOn, todayIso } from "@/lib/dates";
import { SlotDialog } from "@/components/rota/SlotDialog";
import { RotaSetupDialog } from "@/components/rota/RotaSetupDialog";
import { OfferDialog } from "@/components/rota/OfferDialog";
import { SwapsPanel } from "@/components/rota/SwapsPanel";

const POS_CLS = { rider: "bg-yellow-400/15 text-yellow-100 border-l-2 border-yellow-400", controller: "bg-sky-500/15 text-sky-100 border-l-2 border-sky-400" };
const FILL_CLS = (n, total) => (n >= total ? "border-emerald-500/60 bg-emerald-500/[0.07]" : n ? "border-amber-500/60 bg-amber-500/[0.07]" : "border-red-500/60 bg-red-500/[0.07]");

const ShiftCard = ({ t, date, area, slots, me, canSign, canManage, away, offered, onSign, onAssign, onRemove, onOffer }) => {
  const mine = slots.some((s) => s.user_id === me.id);
  const total = t.requirements.reduce((a, r) => a + r.count, 0);
  const full = slots.length >= total;
  return (
    <div data-testid={`shift-${date}-${t.id}-${area.id}`} className={`space-y-2 rounded-xl border p-3 ${FILL_CLS(slots.length, total)}`} style={{ borderLeft: `4px solid ${area.color || "#FEDC19"}` }}>
      <div className="text-xs"><div className="font-bold">{t.name}</div><div className="font-mono text-[11px] text-muted-foreground">{t.start}–{t.end}</div></div>
      {t.requirements.map((r) => {
        const list = slots.filter((s) => s.position === r.key);
        return (
          <div key={r.key} className="text-xs">
            <div className="text-muted-foreground">{r.label} <span className={list.length < r.count ? "text-amber-400" : "text-emerald-400"}>{list.length}/{r.count}</span></div>
            {list.map((s) => (
              <div key={s.id} title={`${s.user_name}${s.vehicle_name ? ` · ${s.vehicle_name}` : ""}`} className={`mt-1 flex items-center justify-between gap-1 rounded-md px-2 py-1 ${POS_CLS[s.position] || "bg-secondary"} ${s.user_id === me.id ? "ring-1 ring-primary" : ""}`}>
                <span className="min-w-0"><span className="block truncate font-semibold">{s.user_name}</span>{s.vehicle_name && <span className="block truncate text-[10px] opacity-80">{s.vehicle_name}</span>}</span>
                <span className="flex shrink-0 items-center gap-1">
                  {offered.has(s.id) && <span data-testid={`slot-swap-badge-${s.id}`} className="rounded bg-violet-500/40 px-1 text-[9px] font-bold uppercase">Swap</span>}
                  {s.user_id === me.id && canSign && !offered.has(s.id) && <button onClick={() => onOffer(s)} data-testid={`slot-offer-${s.id}`} aria-label="Give away shift" title="Give away shift"><Repeat className="h-3 w-3" /></button>}
                  {(s.user_id === me.id || canManage) && <button onClick={() => onRemove(s)} data-testid={`slot-remove-${s.id}`} aria-label="Remove"><X className="h-3 w-3" /></button>}
                </span>
              </div>
            ))}
          </div>
        );
      })}
      <div className="flex gap-1 pt-1">
        {canSign && !mine && !full && !away && <button onClick={onSign} data-testid={`signup-${date}-${t.id}-${area.id}`} className="flex-1 rounded-full bg-primary py-1.5 text-xs font-bold text-primary-foreground">Sign up</button>}
        {canManage && !full && <button onClick={onAssign} data-testid={`assign-${date}-${t.id}-${area.id}`} className="rounded-full border px-2 py-1.5 text-xs" aria-label="Assign"><UserPlus className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
};

export default function Rota() {
  const { user } = useAuth();
  const can = useCan();
  const [start, setStart] = useState(mondayOf(todayIso()));
  const [d, setD] = useState(null);
  const [areaId, setAreaId] = useState("");
  const [members, setMembers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [ctx, setCtx] = useState(null);
  const [setup, setSetup] = useState(false);
  const [offers, setOffers] = useState([]);
  const [offering, setOffering] = useState(null);
  const canManage = can("rota", 3);

  const load = () => api.get("/rota", { params: { start, days: 7 } }).then((r) => {
    api.get("/rota/offers").then((o) => setOffers(o.data)).catch(() => {});
    setD(r.data);
    setAreaId((a) => a || user.areas?.find((x) => r.data.areas.some((y) => y.id === x)) || r.data.areas.find((x) => x.active)?.id || "");
  });
  useEffect(() => { load(); }, [start]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/bikes").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.user_name} from ${s.template_name}?`)) return;
    try { await api.delete(`/rota/slots/${s.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const area = d.areas.find((a) => a.id === areaId);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const offered = new Set(offers.map((o) => o.slot_id));
  const awayOn = (day) => d.unavailable.some((u) => u.user_id === user.id && u.start_date <= day && u.end_date >= day);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Duty rota</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Rota</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setStart(addDays(start, -7))} data-testid="rota-prev" className="rounded-full border p-2" aria-label="Previous week"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="rota-range" className="text-sm font-semibold">{dayLabel(days[0])} – {dayLabel(days[6])}</span>
          <button onClick={() => setStart(addDays(start, 7))} data-testid="rota-next" className="rounded-full border p-2" aria-label="Next week"><ChevronRight className="h-4 w-4" /></button>
          <button onClick={() => setStart(mondayOf(todayIso()))} className="rounded-full border px-3 py-1.5 text-xs">This week</button>
          {canManage && <button onClick={() => setSetup(true)} data-testid="rota-setup-btn" className="rounded-full border p-2" aria-label="Setup"><Settings2 className="h-4 w-4" /></button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pb-1">
        {d.areas.filter((a) => a.active).map((a) => <button key={a.id} onClick={() => setAreaId(a.id)} data-testid={`area-tab-${a.id}`} style={areaId === a.id ? { background: a.color, borderColor: a.color } : { borderColor: a.color }} className={`whitespace-nowrap rounded-full border-2 px-4 py-1.5 text-sm font-semibold ${areaId === a.id ? "text-white" : "text-muted-foreground"}`}>{a.name}</button>)}
      </div>
      <SwapsPanel offers={offers} me={user} canManage={canManage} canSign={can("rota", 2)} areas={d.areas} onChange={load} />
      <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
        <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />Fully staffed</span>
        <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />Part filled</span>
        <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-red-500" />Empty</span>
        <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-yellow-400" />Rider</span>
        <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-sky-400" />Controller</span>
        <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-violet-500" />Up for swap</span>
      </div>
      {area && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7" data-testid="rota-grid">
          {days.map((day) => (
            <div key={day} data-testid={`rota-day-${day}`} className={`space-y-2 rounded-2xl border bg-card p-3 ${day === todayIso() ? "border-primary/60" : ""} ${day < todayIso() ? "hidden sm:block" : ""}`}>
              <div className="flex items-center justify-between"><span className="text-sm font-bold">{dayLabel(day)}</span>{awayOn(day) && <span className="rounded bg-zinc-500/30 px-1.5 text-[10px] font-bold uppercase">Away</span>}</div>
              {d.templates.filter((t) => t.active && runsOn(t, day)).map((t) => (
                <ShiftCard key={t.id} t={t} date={day} area={area} me={user} away={awayOn(day)} canSign={can("rota", 2) && day >= todayIso()} canManage={canManage}
                  slots={d.slots.filter((s) => s.date === day && s.template_id === t.id && s.area_id === area.id)}
                  onSign={() => setCtx({ date: day, template: t, area, slots: d.slots.filter((s) => s.date === day && s.template_id === t.id && s.area_id === area.id) })}
                  onAssign={() => setCtx({ assign: true, date: day, template: t, area, slots: d.slots.filter((s) => s.date === day && s.template_id === t.id && s.area_id === area.id) })}
                  offered={offered} onOffer={setOffering} onRemove={remove} />
              ))}
            </div>
          ))}
        </div>
      )}
      {ctx && <SlotDialog ctx={ctx} members={members} vehicles={vehicles} myVehicles={user.vehicles} onClose={() => setCtx(null)} onSaved={load} />}
      {offering && <OfferDialog slot={offering} members={members} me={user} onClose={() => setOffering(null)} onSaved={load} />}
      {setup && <RotaSetupDialog setup={d} onClose={() => setSetup(false)} onChange={load} />}
    </div>
  );
}
